<template>
  <div>
    <div v-if="isConsole">
      <div v-if="storeInfo.storeTypeCd == '800900000000'">
        <index-dev></index-dev>
      </div>
      <div v-if="storeInfo.storeTypeCd == '800900000001'">
        <index-admin></index-admin>
      </div>
      <div v-if="storeInfo.storeTypeCd == '800900000003'">
        <index-property></index-property>
      </div>
    </div>

    <div v-else v-loading="operationLoading" class="operation-home">
      <main class="content">
        <div class="page-header">
          <h1>全项目经营概览</h1>
          <p>数据更新时间：<span class="date-info">{{ currentDate }}</span></p>
        </div>

        <div class="summary-grid">
          <div v-for="card in summaryCards" :key="card.label" :class="['summary-card', card.className]">
            <div class="card-accent"></div>
            <div class="card-header">
              <span class="card-label">{{ card.label }}</span>
              <div class="card-icon">{{ card.icon }}</div>
            </div>
            <div class="card-value">{{ card.value }}</div>
            <div :class="['card-trend', card.trendClass]">{{ card.trend }}</div>
          </div>
        </div>

        <div class="charts-row">
          <div class="chart-card">
            <div class="chart-title">核心指标金额对比</div>
            <div class="chart-subtitle">总应收 / 完成应收 / 当日实收 / 应收待收（单位：万元）</div>
            <div ref="mainBarChart" class="chart-container"></div>
          </div>
          <div class="chart-card">
            <div class="chart-title">应收构成分析</div>
            <div class="chart-subtitle">完成应收 vs 应收待收 占比</div>
            <div ref="fundPieChart" class="chart-container"></div>
          </div>
        </div>

        <div class="gauge-row">
          <div v-for="gauge in gauges" :key="gauge.ref" class="gauge-card">
            <div class="gauge-title">{{ gauge.title }}</div>
            <div :ref="gauge.ref" class="gauge-container"></div>
            <div class="gauge-value" :style="{ color: gauge.color }">{{ formatPercent(gauge.value) }}</div>
            <div class="gauge-label">{{ gauge.label }}</div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getStoreInfo } from '@/api/user/indexApi'
import { getMyEnteredCommunitys } from '@/api/community/communityApi'
import { queryOperationOverview } from '@/api/report/operationOverviewApi'
import { deepCopy } from '@/utils/vc'
import indexDev from '@/components/index/index-dev.vue'
import indexAdmin from '@/components/index/index-admin.vue'
import indexProperty from '@/components/index/index-property.vue'

export default {
  name: 'index',
  components: {
    indexDev,
    indexAdmin,
    indexProperty
  },
  data() {
    return {
      storeInfo: {
        storeTypeCd: '',
        storeId: ''
      },
      currentDate: '',
      loading: false,
      operationLoading: false,
      charts: [],
      operationMetrics: {
        projectCount: 0,
        totalReceivable: 0,
        completedReceivable: 0,
        todayReceived: 0,
        collectionRate: 0,
        pendingReceivable: 0,
        pendingRate: 0,
        todayCompleteRate: 0
      },
      summaryCards: [
        { label: '项目总数', icon: '🏢', value: '0', trend: '在管项目数量', trendClass: 'trend-neutral', className: 'card-projects' },
        { label: '总应收', icon: '📋', value: '¥0.00', trend: '本年度累计应收', trendClass: 'trend-neutral', className: 'card-receivable' },
        { label: '完成应收', icon: '✅', value: '¥0.00', trend: '已收金额', trendClass: 'trend-up', className: 'card-completed' },
        { label: '当日实收', icon: '📅', value: '¥0.00', trend: '今日累计实收', trendClass: 'trend-up', className: 'card-today' },
        { label: '收费率', icon: '📊', value: '0.00%', trend: '完成应收率', trendClass: 'trend-warn', className: 'card-rate' },
        { label: '应收待收', icon: '⚠️', value: '¥0.00', trend: '待收金额', trendClass: 'trend-down', className: 'card-owing' },
        { label: '待收率', icon: '🔴', value: '0.00%', trend: '应收待收 / 总应收', trendClass: 'trend-down', className: 'card-pending-rate' }
      ],
      gauges: [
        { ref: 'gaugeCollection', title: '收费率', value: 0, color: '#1abc9c', label: '完成应收 / 总应收' },
        { ref: 'gaugePending', title: '待收率', value: 0, color: '#e74c3c', label: '应收待收 / 总应收' },
        { ref: 'gaugeToday', title: '当日完成比', value: 0, color: '#f39c12', label: '当日实收 / 完成应收' }
      ]
    }
  },
  computed: {
    isConsole() {
      return this.$route.path === '/views/index/console'
    }
  },
  watch: {
    isConsole(value) {
      if (value) {
        this.destroyCharts()
        this._loadStoreInfo()
        return
      }
      this.updateCurrentDate()
      this.loadOperationHomeData()
    }
  },
  created() {
    this.updateCurrentDate()
    if (this.isConsole) {
      this._loadStoreInfo()
    }
  },
  mounted() {
    if (!this.isConsole) {
      this.loadOperationHomeData()
    }
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.destroyCharts()
  },
  methods: {
    async _loadStoreInfo() {
      this.loading = true
      try {
        const res = await getStoreInfo()
        deepCopy(res, this.storeInfo)
      } catch (error) {
        console.error('登录失败：', error)
      } finally {
        this.loading = false
      }
    },
    async loadOperationHomeData() {
      this.operationLoading = true
      try {
        const [communityRes, overviewRes] = await Promise.all([
          this.resolveRequest(getMyEnteredCommunitys({ page: 1, row: 1 }), '获取项目统计失败:'),
          this.resolveRequest(queryOperationOverview(), '获取经营概览失败:')
        ])

        const overview = this.getOverviewData(overviewRes)
        this.operationMetrics = {
          projectCount: this.toNumber(communityRes && communityRes.total),
          totalReceivable: overview.receivableAmount,
          completedReceivable: overview.receivedAmount,
          todayReceived: overview.todayReceivedAmount,
          collectionRate: overview.collectionRate,
          pendingReceivable: overview.pendingAmount,
          pendingRate: overview.pendingRate,
          todayCompleteRate: overview.todayCompletionRate
        }
        this.updateOperationHomeView()

        if (this.isConsole) {
          return
        }
        this.$nextTick(this.initCharts)
      } finally {
        this.operationLoading = false
      }
    },
    resolveRequest(promise, errorMessage) {
      return promise.catch(error => {
        console.error(errorMessage, error)
        return null
      })
    },
    getOverviewData(res) {
      const data = res && res.data ? res.data : {}
      return {
        receivableAmount: this.toNumber(data.receivableAmount),
        receivedAmount: this.toNumber(data.receivedAmount),
        todayReceivedAmount: this.toNumber(data.todayReceivedAmount),
        pendingAmount: this.toNumber(data.pendingAmount),
        collectionRate: this.toNumber(data.collectionRate),
        pendingRate: this.toNumber(data.pendingRate),
        todayCompletionRate: this.toNumber(data.todayCompletionRate)
      }
    },
    updateOperationHomeView() {
      const metrics = this.operationMetrics
      this.summaryCards = [
        { label: '项目总数', icon: '🏢', value: String(metrics.projectCount), trend: '在管项目数量', trendClass: 'trend-neutral', className: 'card-projects' },
        { label: '总应收', icon: '📋', value: this.formatCurrency(metrics.totalReceivable), trend: '本年度累计应收', trendClass: 'trend-neutral', className: 'card-receivable' },
        { label: '完成应收', icon: '✅', value: this.formatCurrency(metrics.completedReceivable), trend: '已收金额', trendClass: 'trend-up', className: 'card-completed' },
        { label: '当日实收', icon: '📅', value: this.formatCurrency(metrics.todayReceived), trend: '今日累计实收', trendClass: 'trend-up', className: 'card-today' },
        { label: '收费率', icon: '📊', value: this.formatPercent(metrics.collectionRate), trend: '完成应收率', trendClass: 'trend-warn', className: 'card-rate' },
        { label: '应收待收', icon: '⚠️', value: this.formatCurrency(metrics.pendingReceivable), trend: '待收金额', trendClass: 'trend-down', className: 'card-owing' },
        { label: '待收率', icon: '🔴', value: this.formatPercent(metrics.pendingRate), trend: '应收待收 / 总应收', trendClass: 'trend-down', className: 'card-pending-rate' }
      ]
      this.gauges = [
        { ref: 'gaugeCollection', title: '收费率', value: this.clampPercent(metrics.collectionRate), color: '#1abc9c', label: '完成应收 / 总应收' },
        { ref: 'gaugePending', title: '待收率', value: this.clampPercent(metrics.pendingRate), color: '#e74c3c', label: '应收待收 / 总应收' },
        { ref: 'gaugeToday', title: '当日完成比', value: this.clampPercent(metrics.todayCompleteRate), color: '#f39c12', label: '当日实收 / 完成应收' }
      ]
    },
    updateCurrentDate() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      this.currentDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
    },
    initCharts() {
      this.destroyCharts()
      this.initBarChart()
      this.initPieChart()
      this.gauges.forEach(this.initGaugeChart)
    },
    initBarChart() {
      if (!this.$refs.mainBarChart) return
      const chart = echarts.init(this.$refs.mainBarChart)
      const metrics = this.operationMetrics
      chart.setOption({
        color: ['#9b59b6', '#00b386', '#f39c12', '#e74c3c'],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => `金额: ${params[0].value} 万元`
        },
        grid: { left: 40, right: 20, top: 20, bottom: 35 },
        xAxis: {
          type: 'category',
          data: ['总应收', '完成应收', '当日实收', '应收待收'],
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#eee' } },
          axisLabel: { color: '#555', fontSize: 13 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: 'rgba(0,0,0,0.04)' } },
          axisLabel: { color: '#aaa' }
        },
        series: [{
          type: 'bar',
          barWidth: '45%',
          data: [
            this.toWan(metrics.totalReceivable),
            this.toWan(metrics.completedReceivable),
            this.toWan(metrics.todayReceived),
            this.toWan(metrics.pendingReceivable)
          ],
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: params => ['#9b59b6', '#00b386', '#f39c12', '#e74c3c'][params.dataIndex]
          }
        }]
      })
      this.charts.push(chart)
    },
    initPieChart() {
      if (!this.$refs.fundPieChart) return
      const chart = echarts.init(this.$refs.fundPieChart)
      const metrics = this.operationMetrics
      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: params => `${params.name}: ${this.formatCurrency(params.value)} (${params.percent}%)`
        },
        legend: {
          bottom: 0,
          icon: 'circle',
          textStyle: { color: '#666', fontSize: 13 }
        },
        series: [{
          type: 'pie',
          radius: ['45%', '68%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: { scaleSize: 8 },
          data: [
            { value: metrics.completedReceivable, name: '完成应收', itemStyle: { color: '#00b386' } },
            { value: metrics.pendingReceivable, name: '应收待收', itemStyle: { color: '#e74c3c' } }
          ]
        }]
      })
      this.charts.push(chart)
    },
    initGaugeChart(gauge) {
      const el = this.$refs[gauge.ref]
      if (!el) return
      const chart = echarts.init(Array.isArray(el) ? el[0] : el)
      const value = this.clampPercent(gauge.value)
      chart.setOption({
        series: [{
          type: 'pie',
          radius: ['72%', '88%'],
          center: ['50%', '75%'],
          startAngle: 180,
          data: [
            { value, itemStyle: { color: gauge.color } },
            { value: 100 - value, itemStyle: { color: '#f0f0f0' } },
            { value: 100, itemStyle: { color: 'transparent' }, label: { show: false }, tooltip: { show: false } }
          ],
          label: { show: false },
          emphasis: { disabled: true },
          silent: true
        }]
      })
      this.charts.push(chart)
    },
    resizeCharts() {
      this.charts.forEach(chart => chart.resize())
    },
    destroyCharts() {
      this.charts.forEach(chart => chart.dispose())
      this.charts = []
    },
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    formatCurrency(value) {
      return `¥${this.toNumber(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatPercent(value) {
      return `${this.toNumber(value).toFixed(2)}%`
    },
    toWan(value) {
      const wan = this.toNumber(value) / 10000
      if (Math.abs(wan) > 0 && Math.abs(wan) < 0.01) {
        return Number(wan.toFixed(4))
      }
      return Number(wan.toFixed(2))
    },
    clampPercent(value) {
      return Math.min(Math.max(this.toNumber(value), 0), 100)
    }
  }
}
</script>

<style scoped>
.operation-home {
  min-height: calc(100vh - 60px);
  background: #f0f2f5;
  color: #333;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
}

.content {
  height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 4px;
}

.page-header p {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.date-info {
  color: #00b386;
  font-weight: 500;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
  position: relative;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  border-radius: 4px 0 0 4px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-label {
  font-size: 13px;
  color: #888;
  font-weight: 400;
}

.card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.card-value {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.card-trend {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend-up { color: #00b386; }
.trend-down { color: #e74c3c; }
.trend-neutral { color: #999; }
.trend-warn { color: #e67e22; }

.card-projects .card-accent { background: #3498db; }
.card-projects .card-icon { background: rgba(52,152,219,0.1); color: #3498db; }
.card-projects .card-value { color: #3498db; }

.card-receivable .card-accent { background: #9b59b6; }
.card-receivable .card-icon { background: rgba(155,89,182,0.1); color: #9b59b6; }
.card-receivable .card-value { color: #9b59b6; }

.card-completed .card-accent { background: #00b386; }
.card-completed .card-icon { background: rgba(0,179,134,0.1); color: #00b386; }
.card-completed .card-value { color: #00b386; }

.card-today .card-accent { background: #f39c12; }
.card-today .card-icon { background: rgba(243,156,18,0.1); color: #f39c12; }
.card-today .card-value { color: #f39c12; }

.card-rate .card-accent { background: #1abc9c; }
.card-rate .card-icon { background: rgba(26,188,156,0.1); color: #1abc9c; }
.card-rate .card-value { color: #1abc9c; }

.card-owing .card-accent { background: #e74c3c; }
.card-owing .card-icon { background: rgba(231,76,60,0.1); color: #e74c3c; }
.card-owing .card-value { color: #e74c3c; }

.card-pending-rate .card-accent { background: #e67e22; }
.card-pending-rate .card-icon { background: rgba(230,126,34,0.1); color: #e67e22; }
.card-pending-rate .card-value { color: #e67e22; }

.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.chart-card,
.gauge-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.chart-subtitle {
  font-size: 12px;
  color: #aaa;
  margin-bottom: 16px;
}

.chart-container {
  width: 100%;
  height: 300px;
}

.gauge-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.gauge-card {
  text-align: center;
}

.gauge-title {
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin-bottom: 12px;
}

.gauge-container {
  width: 100%;
  height: 160px;
}

.gauge-value {
  font-size: 22px;
  font-weight: 700;
  margin-top: 8px;
}

.gauge-label {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.content::-webkit-scrollbar {
  width: 6px;
}

.content::-webkit-scrollbar-track {
  background: transparent;
}

.content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-row,
  .gauge-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
